<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import NavBar from '@/components/NavBar'
import {usePreviousConference} from '@/composables/usePreviousConference'
import PreviousConferenceHeader from '@/components/previous-conference/PreviousConferenceHeader'
import placeholderImage from '@/assets/placeholder.jpg'

const contentData = inject('contentData')
const route = useRoute()
const selectedPhoto = ref(null)

const {
    previousConference,
    loading,
    error,
    fetchPreviousConferenceByYear,
} = usePreviousConference()

/**
 * Input example: route.params.year = "2024"
 * Output example: previousConference.value = { id: 3, date: "2024", galleries: [{ id: 1, title: "Poster Session", photos: [] }] }
 */
const loadPreviousConference = async () => {
    await fetchPreviousConferenceByYear(route.params.year)
}

const visibleGalleries = computed(() => {
    const galleries = previousConference.value?.galleries || []
    return galleries.filter(gallery => Array.isArray(gallery.photos) && gallery.photos.length > 0)
})

/**
 * Input example: photo = { id: 8, image: "/media/photos/opening.jpg", title: "Opening session" }
 * Output example: selectedPhoto.value = { id: 8, image: "/media/photos/opening.jpg", title: "Opening session" }
 */
const openPhotoModal = photo => {
    selectedPhoto.value = photo
}

/**
 * Input example: selectedPhoto.value = { id: 8, image: "/media/photos/opening.jpg" }
 * Output example: selectedPhoto.value = null
 */
const closePhotoModal = () => {
    selectedPhoto.value = null
}

onMounted(async () => {
    await loadPreviousConference()
})

watch(
    () => route.params.year,
    async () => {
        closePhotoModal()
        await loadPreviousConference()
    },
)
</script>

<template>
    <div class="min-h-screen bg-white">
        <NavBar />

        <PreviousConferenceHeader
            :background-image-path="contentData.background_image_path"
            :current-conference-label="previousConference?.date || 'Previous Conference'" />

        <section class="relative">
            <div class="container px-4 mx-auto py-6 sm:py-10">
                <div class="border-b border-slate-200 pb-3 sm:pb-4">
                    <div class="inline-flex border-b-2 border-teal-700 pb-3 text-sm sm:text-base font-semibold text-teal-700">
                        {{ previousConference?.date || 'Previous Conference' }} Galleries
                    </div>
                </div>

                <div class="py-8 sm:py-12">
                    <div v-if="loading" class="text-slate-700">
                        Loading galleries...
                    </div>

                    <div v-else-if="error" class="text-red-700">
                        {{ error }}
                    </div>

                    <div v-else-if="visibleGalleries.length" class="space-y-10 sm:space-y-16">
                        <section
                            v-for="gallery in visibleGalleries"
                            :key="gallery.id"
                            class="space-y-5 sm:space-y-6">
                            <div class="mx-auto max-w-3xl text-center">
                                <h2 class="font-heading text-2xl text-slate-900 uppercase sm:text-4xl">
                                    {{ gallery.title }}
                                </h2>
                                <div class="mx-auto mt-4 h-1 w-16 bg-teal-700"></div>
                            </div>

                            <div
                                v-if="gallery.photos?.length"
                                class="grid gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
                                <button
                                    v-for="photo in gallery.photos"
                                    :key="photo.id"
                                    type="button"
                                    class="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                    @click="openPhotoModal(photo)">
                                    <img
                                        :src="photo.image || placeholderImage"
                                        :alt="photo.title || gallery.title"
                                        class="aspect-[4/3] w-full object-cover" />
                                </button>
                            </div>
                        </section>
                    </div>

                    <div v-else class="text-slate-700">
                        No galleries available for this conference yet.
                    </div>
                </div>
            </div>
        </section>

        <div
            v-if="selectedPhoto"
            class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-8"
            @click="closePhotoModal">
            <div
                class="relative max-h-full w-full max-w-5xl"
                @click.stop>
                <button
                    type="button"
                    class="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-sm transition hover:bg-white"
                    @click="closePhotoModal">
                    <span class="text-xl leading-none">&times;</span>
                </button>

                <img
                    :src="selectedPhoto.image || placeholderImage"
                    :alt="selectedPhoto.title || previousConference?.date || 'Gallery photo'"
                    class="max-h-[85vh] w-full rounded-md object-contain shadow-2xl" />
            </div>
        </div>
    </div>
</template>

<style scoped></style>
